/**
 * Helper functions pentru verificare plan premium / Healthypaws+
 */

// Tip User din AuthContext (extras din context)
type User = {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  role?: string;
  status?: string;
  clinicId?: string;
  clinicName?: string;
  shelterId?: string;
  shelterName?: string;
  createdAt?: string;
  planFeatures?: string[];
  activePlan?: {
    id: string;
    name: string;
    features: string[];
    maxUsers: number;
    maxAnimals: number;
    maxAppointments: number;
    endDate: string;
  };
  clinic?: {
    id: string;
    name: string;
    cui: string;
    email: string;
    phone: string;
    address: {
      street: string;
      streetNumber: string;
      city: string;
      county: string;
      postalCode: string;
      country?: string;
    };
    logo?: string;
    status: string;
  };
};

/**
 * Verifică dacă utilizatorul are plan premium (Pro sau Premium pentru utilizatori)
 * sau Professional/Enterprise pentru clinică
 */
export const isPremiumUser = (user: User | null): boolean => {
  if (!user) return false;
  
  // Master admin are acces la toate funcționalitățile premium
  if (user.role === 'master_admin') return true;
  
  // Verifică planul activ pentru utilizatori
  if (user.activePlan) {
    const planName = user.activePlan.name?.toLowerCase() || '';
    
    // Pentru utilizatori: Pro sau Premium
    if (planName.includes('pro') || planName.includes('premium')) {
      return true;
    }
    
    // Pentru clinică: Professional sau Enterprise
    if (planName.includes('professional') || planName.includes('enterprise')) {
      return true;
    }
  }
  
  return false;
};

/**
 * Verifică dacă utilizatorul are plan Basic (nu premium)
 */
export const isBasicUser = (user: User | null): boolean => {
  if (!user) return false;
  
  if (user.activePlan) {
    const planName = user.activePlan.name?.toLowerCase() || '';
    return planName.includes('basic');
  }
  
  // Dacă nu are plan activ, consideră-l Basic
  return true;
};

/**
 * Obține numele planului formatat pentru display
 */
export const getPlanDisplayName = (user: User | null): string => {
  if (!user?.activePlan) return 'Basic';
  
  const planName = user.activePlan.name || 'Basic';
  
  // Formatare pentru display
  if (planName.toLowerCase().includes('premium')) {
    return 'Healthypaws+ Premium';
  }
  if (planName.toLowerCase().includes('pro')) {
    return 'Healthypaws+ Pro';
  }
  if (planName.toLowerCase().includes('professional')) {
    return 'Professional';
  }
  if (planName.toLowerCase().includes('enterprise')) {
    return 'Enterprise';
  }
  
  return planName;
};

/**
 * Verifică dacă planul include o funcționalitate premium specifică
 */
export const hasPremiumFeature = (user: User | null, feature: string): boolean => {
  if (!user) return false;
  
  // Master admin are toate funcționalitățile
  if (user.role === 'master_admin') return true;
  
  // Verifică în planFeatures
  if (user.planFeatures && user.planFeatures.includes(feature)) {
    return true;
  }
  
  // Verifică în activePlan.features
  if (user.activePlan?.features && user.activePlan.features.includes(feature)) {
    return true;
  }
  
  return false;
};

/**
 * Lista funcționalităților premium care necesită upgrade
 */
export const PREMIUM_FEATURES = {
  // Pentru utilizatori (clienți)
  CLIENT_EXTENDED_HISTORY: 'client.extended_history',
  CLIENT_UNLIMITED_ANIMALS: 'client.unlimited_animals',
  CLIENT_PRIORITY_BOOKING: 'client.priority_booking',
  CLIENT_ADVANCED_NOTIFICATIONS: 'client.advanced_notifications',
  CLIENT_EXPORT_DATA: 'client.export_data',
  
  // Pentru clinică
  CLINIC_ADVANCED_REPORTS: 'clinic.advanced_reports',
  CLINIC_API_ACCESS: 'clinic.api_access',
  CLINIC_INTEGRATIONS: 'clinic.integrations',
  CLINIC_MULTI_LOCATION: 'clinic.multi_location',
  CLINIC_AUTOMATION: 'clinic.automation'
} as const;

